import React, { useEffect, useMemo } from 'react';
import './App.css';
import { GateMainProvider } from './GateMainContext';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Login from './Login/LoginScene';
import RedirectHandler from './Login/RedirectHandler';
import Portal from './Portal';
import ToolIFrame from './ToolIFrame';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CreateGateTheme from './Shared/CreateGateTheme';
import ProtectedRoute from './Shared/ProtectedRoute';
import Signup from './Users/Signup';
import AdminScene from './Users/AdminScene';
import UserScene from './Users/UserScene';
import UserResetPasswordScene from './Users/UserResetPasswordScene';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import { useGateMainContext } from './GateMainContext';

export function GateAppShell() {
  const { branding } = useGateMainContext();
  const defaultTitle = useMemo(() => document.title || 'SG WebTools', []);
  const theme = useMemo(
    () => CreateGateTheme(branding?.primaryColor ?? undefined, branding?.secondaryColor ?? undefined),
    [branding?.primaryColor, branding?.secondaryColor]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', theme.palette.primary.main);
    root.style.setProperty('--brand-primary-dark', theme.palette.primary.dark);
    root.style.setProperty('--brand-primary-light', theme.palette.primary.light);
    root.style.setProperty('--brand-secondary', theme.palette.secondary.main);
    root.style.setProperty('--brand-background', theme.palette.background.default ?? '#F5F5F5');
    root.style.setProperty('--brand-surface', theme.palette.background.paper ?? '#FFFFFF');
    root.style.setProperty('--brand-text-primary', theme.palette.text.primary);
    root.style.setProperty('--brand-divider', theme.palette.divider);
    document.body.style.backgroundColor = theme.palette.background.default ?? '#F5F5F5';

    const metaThemeColor = document.querySelector<HTMLMetaElement>("meta[name='theme-color']");
    if (metaThemeColor) {
      metaThemeColor.content = theme.palette.primary.main;
    }
  }, [theme]);

  useEffect(() => {
    const url = branding?.faviconUrl;
    if (!url) {
      return;
    }

    const isSafe = /^(https?:\/\/|\/|data:image\/)/i.test(url) && !/^javascript:/i.test(url);
    if (!isSafe) {
      return;
    }

    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    const prev = link.href;
    link.href = url;
    return () => {
      if (link) {
        link.href = prev;
      }
    };
  }, [branding?.faviconUrl]);

  useEffect(() => {
    document.title = branding?.brandName?.trim() || defaultTitle;
  }, [branding?.brandName, defaultTitle]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/login/:tenantSlug" element={<Login />} />
          <Route path="/loginframe/:lang/:server/:redirect" element={<Login externalLogin />} />
          <Route path="/loginframe/:server/:redirect" element={<Login externalLogin />} />
          <Route path="auth-callback/:hash" element={<RedirectHandler />} />
          <Route path="auth-callback/" element={<RedirectHandler />} />
          <Route path='portal' element={<ProtectedRoute><Portal /></ProtectedRoute>} />
          <Route path='portal/:lang' element={<ProtectedRoute><Portal /></ProtectedRoute>} />
          <Route path='tool/:id' element={<ToolIFrame />} />
          <Route path='usersignup' element={<Signup />} />
          <Route path="admin" element={<ProtectedRoute><AdminScene isActualTenant={false} /></ProtectedRoute>} />
          <Route path="tenant" element={<ProtectedRoute><AdminScene isActualTenant={true} /></ProtectedRoute>} />
          <Route path="user" element={<ProtectedRoute><UserScene /></ProtectedRoute>} />
          <Route path="resetpassword" element={<UserResetPasswordScene />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function App() {
  const [languageInitialized, setLanguageInitialized] = React.useState(false);
  useEffect(() => {
    i18n
      // load translation using http -> see /public/locales
      // learn more: https://github.com/i18next/i18next-http-backend
      .use(Backend)
      // detect user language
      // learn more: https://github.com/i18next/i18next-browser-languageDetector
      .use(LanguageDetector)
      // pass the i18n instance to react-i18next.
      .use(initReactI18next)
      // init i18next
      // for all options read: https://www.i18next.com/overview/configuration-options
      .init({
        fallbackLng: 'cs',
        // Only Czech and English locales exist. Collapse region variants (e.g. en-US, en-GB)
        // to the base language so i18n.language matches the LanguagePicker's codes ('cs'/'en')
        // and unsupported browser languages fall back cleanly to Czech.
        supportedLngs: ['cs', 'en'],
        nonExplicitSupportedLngs: true,
        load: 'languageOnly',
        debug: true,

        interpolation: {
          escapeValue: false, // not needed for react as it escapes by default
        },
      }, () => {
        setLanguageInitialized(true);
      });
  }, []);
  return (
    languageInitialized ? <GateMainProvider><GateAppShell /></GateMainProvider> : null
  );
}

export default App;
